import { useRef, type ReactNode } from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { useOnClickOutside } from "usehooks-ts";

import LoadingRipple from "../LoadingRipple";
import Body from "./Body";
import Footer from "./Footer";
import Header from "./Header";
import ModalContext from "./ModalContext";
import TextBold from "./TextBold";

const modal = cva(
  [
    "relative w-full transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg",
  ],
  {
    variants: {
      variant: {
        default: [""],
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
);

export interface ModalProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof modal> {
  header?: ReactNode;
  body?: ReactNode;
  footer?: ReactNode;
  show?: boolean;
  setShow?: React.Dispatch<boolean>;
  onClose?: () => unknown;
  grayBackground?: boolean;
  closeOnClickOutside?: boolean;
}

const Modal = ({
  header,
  body,
  footer,
  className,
  variant,
  show = true,
  setShow,
  grayBackground = true,
  onClose,
  closeOnClickOutside,
  ...props
}: ModalProps) => {
  if (!show) return null;
  const modalRef = useRef<HTMLDivElement>(null);

  function closeModal() {
    if (setShow) setShow(false);
    if (onClose) onClose();
  }

  return (
    <ModalContext.Provider value={{ show, closeModal }}>
      <div
        className="relative z-30"
        aria-labelledby="modal-title"
        role="dialog"
        aria-modal="true"
      >
        {grayBackground && (
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"></div>
        )}
        <div className="fixed inset-0 z-10 overflow-y-auto" ref={modalRef}>
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0 ">
            <div className={modal({ className, variant })}>
              {header && (
                <div className="border-b border-slate-300 bg-white p-5">
                  {header}
                </div>
              )}
              {body && <div className="bg-white p-5">{body}</div>}
              {footer && (
                <div className="border-b border-slate-300 bg-white p-5">
                  {footer}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </ModalContext.Provider>
  );
};

Modal.Header = Header;
Modal.Body = Body;
Modal.Footer = Footer;
Modal.Loading = LoadingRipple;
Modal.Bold = TextBold;

export default Modal;
